import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { UserPermissionService } from '../services/user-permission.service';
import { AuthService } from '../services/auth.service';

/**
 * Route guard that checks if the user has the required permission(s).
 * 
 * Usage in routes:
 * {
 *   path: 'users/create',
 *   loadComponent: () => import('./create-user.component'),
 *   canActivate: [permissionGuard],
 *   data: { permission: 'users.create' }
 * }
 * 
 * For multiple permissions:
 * {
 *   path: 'admin',
 *   loadComponent: () => import('./admin.component'),
 *   canActivate: [permissionGuard],
 *   data: { permissions: ['admin.read', 'settings.read'], mode: 'any' }
 * }
 */
export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot): boolean | UrlTree => {
    const userPermissionService = inject(UserPermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);

    if (authService.isLoggingOut) {
        return router.parseUrl('/session-expired');
    }

    // Get required permission(s) from route data
    const permission = route.data['permission'] as string | undefined;
    const permissions = route.data['permissions'] as string[] | undefined;
    const mode = (route.data['mode'] as 'any' | 'all') || 'any';

    // If no permission required, allow access
    if (!permission && (!permissions || permissions.length === 0)) {
        return true;
    }

    // Check single permission
    if (permission) {
        if (userPermissionService.hasPermission(permission)) {
            return true;
        }
    }

    // Check multiple permissions
    if (permissions && permissions.length > 0) {
        const hasAccess = mode === 'all'
            ? userPermissionService.hasAllPermissions(permissions)
            : userPermissionService.hasAnyPermission(permissions);

        if (hasAccess) {
            return true;
        }
    }


    if (!authService.isAuthenticated) {
        return router.parseUrl('/auth/login');
    }

    return router.parseUrl('/access-denied');
};

/**
 * Route guard that requires the user to have a role assigned.
 * Redirects to contact-admin page if no role is assigned.
 */
export const requireRoleGuard: CanActivateFn = (): boolean | UrlTree => {
    const userPermissionService = inject(UserPermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);

    if (authService.isLoggingOut) {
        return router.parseUrl('/session-expired');
    }

    if (userPermissionService.hasRole() || userPermissionService.isHostAdmin()) {
        return true;
    }

    return router.parseUrl('/contact-admin');
};

/**
 * Route guard that requires Host Admin access.
 */
export const hostAdminGuard: CanActivateFn = (): boolean | UrlTree => {
    const userPermissionService = inject(UserPermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);

    if (authService.isLoggingOut) {
        return router.parseUrl('/session-expired');
    }

    if (!authService.isAuthenticated) {
        return router.parseUrl('/auth/login');
    }

    if (userPermissionService.isHostAdmin()) {
        return true;
    }

    return router.parseUrl('/access-denied');
};
